// this method simply takes the project/leakage/company data and generates all placeholder values that will be used in
import { TFunction } from 'i18next'
import { Building, Company, isLeakageResolved, Leakage, Project, User } from '../../../interfaces/BusinessObjects'
import { ReportRequest, ReportType } from '../../helpers/reportGeneration/Types'
import { getFirstLastLeakages, getLeakageDate } from './logic'
import { calcDifferenceInDays, dateToLocalDateString, getIsoCode } from '../../helpers/dates'
import { enumValToExportVal, Unit } from '../../../interfaces/Units'
import { getLabelForCalcCostBaseFromProject } from '../../helpers/projectUtils'
import {
  convertAmount,
  calcCo2PerYear,
  calcCo2AmountPerYear,
  calcCost,
  calcEnergyUsagePerYear,
  calcRelative,
  calcTotalSumsForLeakages,
  convertProjectSpecificPower,
  getCo2EmissionsPowerGeneration,
  getCo2UnitLabel,
  getCostTime,
  getCostUnit,
  getEnergyAmount,
  getEnergyCost,
  getPressureCost,
  totalCosts,
  calcLeakageEnergyUsagePerYear,
  calcLeakVolAmount,
  calcVolUsagePerYear,
  getAmountValues,
  calcProjectLeakVolumePerYear,
  calcEnergyFromLeakSums,
} from '../../helpers/calculations'
import { PersistedBuilding } from '../../../electronShared/domain/types/types'

const csLogoBase64 =
  'data:image/png;base64,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'

const addLabelToMap = (valueMap: Record<string, string>, label: string, t: TFunction): void => {
  valueMap[`label-${label.split('.').pop()}`] = t(label)
}

export const addLabelsToMap = (valueMap: Record<string, string>, t: TFunction): void => {
  addLabelToMap(valueMap, 'reports.leakageReport', t)
  addLabelToMap(valueMap, 'reports.contact', t)
  addLabelToMap(valueMap, 'reports.contactData', t)
  addLabelToMap(valueMap, 'reports.client', t)
  addLabelToMap(valueMap, 'reports.auditor', t)
  addLabelToMap(valueMap, 'reports.begin', t)
  addLabelToMap(valueMap, 'reports.end', t)
  addLabelToMap(valueMap, 'reports.duration', t)
  addLabelToMap(valueMap, 'reports.projectMaster', t)
  addLabelToMap(valueMap, 'reports.results', t)
  addLabelToMap(valueMap, 'reports.importDate', t)
  addLabelToMap(valueMap, 'reports.pressureCost', t)
  addLabelToMap(valueMap, 'reports.co2Total', t)
  addLabelToMap(valueMap, 'reports.nResolved', t)
  addLabelToMap(valueMap, 'reports.savedLeakage', t)
  addLabelToMap(valueMap, 'reports.costSavedPerYear', t)
  addLabelToMap(valueMap, 'reports.co2Saved', t)
  addLabelToMap(valueMap, 'reports.relativePercentage', t)
  addLabelToMap(valueMap, 'reports.dateTime', t)
  addLabelToMap(valueMap, 'reports.logo', t)
  addLabelToMap(valueMap, 'reports.csBrandingFooter', t)
  addLabelToMap(valueMap, 'reports.csLink', t)
  addLabelToMap(valueMap, 'reports.improvements', t)
  addLabelToMap(valueMap, 'reports.dayS', t)
  addLabelToMap(valueMap, 'reports.buildingPlace', t)
  addLabelToMap(valueMap, 'reports.leakageAmount', t)
  addLabelToMap(valueMap, 'reports.co2PerYear', t)
  addLabelToMap(valueMap, 'reports.energyPerYear', t)
  addLabelToMap(valueMap, 'reports.savedEnergyPerYear', t)
  addLabelToMap(valueMap, 'reports.page', t)
  addLabelToMap(valueMap, 'objects.company', t)
  addLabelToMap(valueMap, 'address.address', t)
  addLabelToMap(valueMap, 'contact.email', t)
  addLabelToMap(valueMap, 'contact.phone', t)
  addLabelToMap(valueMap, 'project.workingHours', t)
  addLabelToMap(valueMap, 'project.specificPower', t)
  addLabelToMap(valueMap, 'project.co2', t)
  addLabelToMap(valueMap, 'project.nLeakages', t)
  addLabelToMap(valueMap, 'project.totalLeakage', t)
  addLabelToMap(valueMap, 'leakage.costPerYear', t)
  addLabelToMap(valueMap, 'leakage.costPerVolume', t)
  addLabelToMap(valueMap, 'objects.building', t)
  addLabelToMap(valueMap, 'priority.priority', t)
  addLabelToMap(valueMap, 'leakage.resolvableUnderPressure', t)
  addLabelToMap(valueMap, 'leakage.failure', t)
  addLabelToMap(valueMap, 'leakage.leakTag', t)
  addLabelToMap(valueMap, 'leakage.sparePart', t)
  addLabelToMap(valueMap, 'leakage.measure', t)
  addLabelToMap(valueMap, 'leakage.note', t)
  addLabelToMap(valueMap, 'leakage.status', t)
  addLabelToMap(valueMap, 'leakage.solvedAt', t)
  addLabelToMap(valueMap, 'leakage.solvedBy', t)
  addLabelToMap(valueMap, 'leakage.comment', t)
  addLabelToMap(valueMap, 'leakage.co2Unit', t)
  addLabelToMap(valueMap, 'reports.electricityRate', t)
  addLabelToMap(valueMap, 'leakage.energyConsumed', t)
  addLabelToMap(valueMap, 'leakage.volumePerYear', t)
  addLabelToMap(valueMap, 'leakage.savedVolumePerYear', t)
}
const TEMPLATE_FILENAME_XLSX = 'report.xlsx'
const TEMPLATE_FILENAME_HTML = 'report.handlebars'
const updateValueMap = (valueMap: Record<string, string>, key: string, value: string | undefined): void => {
  valueMap[key] = value ?? ''
}
const generateMultiValueMap = (
  leakages: Leakage[],
  buildings: Building[],
  c: Company,
  project: Project,
  t: TFunction,
  precision?: number,
  fixed?: number,
): Record<string, Record<string, string[]>> => {
  //const usedLeakages = [...leakages].sort((a, b) => parseInt(a.leakTag || '') - parseInt(b.leakTag || ''))
  const usedLeakages = [...leakages]
  const date = usedLeakages.map((l) => dateToLocalDateString(new Date(l.date ?? ''), getIsoCode(t)))
  const building = usedLeakages.map((l) => {
    const buildingName = buildings.filter((building) => building.id === l.buildingId).pop()?.name
    return buildingName ? `${buildingName} ${l.place?.name}` : ''
  })
  const time = usedLeakages.map((l) => l.time ?? '')
  const resLeakage = usedLeakages.map(
    (l) => convertAmount(l.resLeakage, l.resUnitL, project.unitV, t, precision, fixed).resultAsLocalFormat,
  )
  const resUnitL = usedLeakages.map(
    (l) => convertAmount(l.resLeakage, l.resUnitL, project.unitV, t, precision, fixed).unitAsLabel,
  )
  const resLeakageNm3 = usedLeakages.map(
    (l) => convertAmount(l.resLeakage, l.resUnitL, Unit.NM3_HOUR, t, precision, fixed).text,
  )
  const leakEnergyValue = usedLeakages.map(
    (l) => calcLeakageEnergyUsagePerYear(l, project, t, precision, fixed)?.text ?? '',
  )
  const leakEnergyUnit = usedLeakages.map(
    (l) => calcLeakageEnergyUsagePerYear(l, project, t, precision, fixed)?.unitAsEnum ?? '',
  )
  const leakVolumePerYear = usedLeakages.map(
    (l) => calcLeakVolAmount(l.resLeakage, l.resUnitL, l.costTime, project.unitV, t, precision, fixed)?.text ?? '',
  )
  const pressure = usedLeakages.map((l) => (l.commRup ? t(l.commRup) : ''))
  const lossCosts = usedLeakages.map((l) => calcCost(l, project, t, precision, fixed).text)
  const lossCostsValue = usedLeakages.map((l) => calcCost(l, project, t, precision, fixed).resultAsFormattedString)
  const costUnit = usedLeakages.map((l) => getCostUnit(project, l).label)
  const priority = usedLeakages.map((l) => enumValToExportVal(l.priority))
  const comment = usedLeakages.map((l) => l.comment ?? '')
  const commF = usedLeakages.map((l) => l.commF ?? '')
  const commR = usedLeakages.map((l) => l.commR ?? '')
  const commM = usedLeakages.map((l) => l.commM ?? '')
  const note = usedLeakages.map((l) => l.note ?? '')
  const status = usedLeakages.map((l) => t(l.status ?? 'actions.inProgress'))
  const solved_at = usedLeakages.map((l) =>
    l.solved_at ? dateToLocalDateString(new Date(l.solved_at), getIsoCode(t)) : '',
  )
  const solved_by = usedLeakages.map((l) => l.solved_by ?? '')
  const leakTag = usedLeakages.map((l) => l.leakTag ?? '')
  const company = usedLeakages.map(() => c.name ?? '')
  const co2PerYear = usedLeakages.map((l) => calcCo2PerYear(l, project, t, precision, fixed).resultAsLocalFormat)
  const co2PerYearWithUnitLabel = usedLeakages.map((l) => calcCo2AmountPerYear([l], project, t, precision, fixed).text)
  const images = usedLeakages.map((l) => (l.imagesAsBase64 || []).filter((i) => i.checked))
  const firstImages = images.map((i) => (i.length > 0 ? i[0].src : ''))
  const secondImages = images.map((i) => (i.length > 1 ? i[1].src : ''))
  const leakCostTime = usedLeakages.map((l) => getCostTime(undefined, l, t).text)
  const showLeakCostTime = usedLeakages.map(() => (project.costTime ? 'false' : 'true'))
  //updateValueMap(valueMap, 'showLeakCostTime', project?.costTime && project.costTime ? 'false' : 'true')

  const leakageMaps = {
    pressure,
    building,
    date,
    time,
    resLeakage,
    resLeakageNm3,
    resUnitL,
    leakTag,
    lossCosts,
    costUnit,
    priority,
    comment,
    commF,
    commR,
    commM,
    note,
    status,
    solved_at,
    solved_by,
    company,
    co2PerYear,
    co2PerYearWithUnitLabel,
    'IMAGE-first': firstImages,
    'IMAGE-second': secondImages,
    lossCostsValue,
    leakEnergyValue,
    leakVolumePerYear,
    leakEnergyUnit,
    leakCostTime,
    showLeakCostTime,
  }
  return { leakages: leakageMaps }
}

// the variables in the template XLSX document
export const generateReportRequest = (
  project: Project,
  leakages: Leakage[],
  buildings: Building[] | PersistedBuilding[],
  company: Company,
  user: User,
  type: ReportType,
  t: TFunction,
  companyPath?: string,
): ReportRequest => {
  const valueMap: Record<string, string> = {}

  const [firstLeakage, lastLeakage] = getFirstLastLeakages(leakages)
  const [firstDate, lastDate] = [getLeakageDate(firstLeakage), getLeakageDate(lastLeakage)]
  const resolvedLeakages = leakages.filter((l) => isLeakageResolved(l))

  const totalEnergyUnitValues = calcEnergyFromLeakSums(leakages, project, t)
  const projectSavedEnergyUnitValues = calcEnergyFromLeakSums(resolvedLeakages, project, t)

  const projectTotalVolumeUnitValue = calcProjectLeakVolumePerYear(leakages, project, t)
  const projectSavedVolumeUnitValue = calcProjectLeakVolumePerYear(resolvedLeakages, project, t)

  const getTotalEnergy = (): string => {
    const projectMasterEnergyTotal = calcEnergyUsagePerYear(totalSumsForLeakage, project, t, 'sumAmount', 2, 2)
    return getAmountValues(totalEnergyUnitValues, projectMasterEnergyTotal)
  }
  const getSavedEnergy = (): string => {
    const projectMasterSavedEnergy = calcEnergyUsagePerYear(totalSumsForLeakage, project, t, 'savedAmount', 2, 2)
    return getAmountValues(projectSavedEnergyUnitValues, projectMasterSavedEnergy)
  }
  const getTotalVolume = (): string => {
    const projectMasterTotalVolume = calcVolUsagePerYear(totalSumsForLeakage, project, t, 'sumAmount', 2, 2)
    return getAmountValues(projectTotalVolumeUnitValue, projectMasterTotalVolume)
  }
  const getSavedVolume = (): string => {
    const projectMasterSavedVolume = calcVolUsagePerYear(totalSumsForLeakage, project, t, 'savedAmount', 2, 2)
    return getAmountValues(projectSavedVolumeUnitValue, projectMasterSavedVolume)
  }
  valueMap['date-begin'] = dateToLocalDateString(getLeakageDate(firstLeakage), getIsoCode(t))
  valueMap['date-end'] = dateToLocalDateString(getLeakageDate(lastLeakage), getIsoCode(t))
  valueMap['days'] = `${calcDifferenceInDays(lastDate, firstDate)}`
  const compName = !companyPath ? company.name : `${t('objects.path')}: ${companyPath}, ` + company.name
  updateValueMap(valueMap, 'client-company', compName)
  updateValueMap(
    valueMap,
    'client-address',
    `${company.address?.street ?? ''} ${company.address?.number ? company.address?.number + ',' : ''} ${
      company.address?.zip ?? ''
    } ${company.address?.city ?? ''}`,
  )
  updateValueMap(
    valueMap,
    'client-contact-name',
    company.contact && company.contact.prename && company.contact.surname
      ? company.contact.prename + ' ' + company.contact.surname
      : '',
  )

  // only add in expert mode
  // @todo calculation
  const projectCostCalcBaseLabel = getLabelForCalcCostBaseFromProject(project?.costCalcBase, project?.cMod, t)
  addLabelsToMap(valueMap, t)
  if (projectCostCalcBaseLabel) {
    addLabelToMap(valueMap, 'reports.costCalculationBase', t)
    updateValueMap(valueMap, 'costCalculationBaseValue', projectCostCalcBaseLabel)
  }

  updateValueMap(valueMap, 'client-contact-email', company.contact?.email)
  updateValueMap(valueMap, 'client-contact-phone', company.contact?.phone)
  updateValueMap(valueMap, 'IMAGE-client-logo', company.logo)
  updateValueMap(valueMap, 'IMAGE-cs-logo', csLogoBase64)
  updateValueMap(
    valueMap,
    'auditor-contact-name',
    user.prename && user.surname ? user.prename + ' ' + user.surname : '',
  )
  updateValueMap(valueMap, 'auditor-contact-email', user.email)
  updateValueMap(valueMap, 'auditor-contact-phone', user.phone)
  updateValueMap(
    valueMap,
    'auditor-address',
    `${user.street ?? ''} ${user.number ? user.number + ',' : ''} ${user.zip ?? ''} ${user.city ?? ''}`,
  )
  updateValueMap(valueMap, 'auditor-company', user.company)
  updateValueMap(valueMap, 'IMAGE-auditor-logo', user.image)
  updateValueMap(
    valueMap,
    'created',
    dateToLocalDateString(project.created ? new Date(project.created) : new Date(), getIsoCode(t)),
  )

  updateValueMap(valueMap, 'pressureCost', getPressureCost(project, t))

  const costTime = getCostTime(project, undefined, t, 2, 2)
  updateValueMap(valueMap, 'costTime', project.costTime ? costTime.text : '')

  const energyCost = getEnergyCost(project, t, 2, 2)
  updateValueMap(valueMap, 'energyCost', energyCost.text)
  updateValueMap(valueMap, 'showEnergyCost', energyCost.result > 0 ? 'true' : '')

  const co2AmountPerYear = calcCo2AmountPerYear(leakages, project, t, 2, 2)
  const savedCo2AmountPerYear = calcCo2AmountPerYear(resolvedLeakages, project, t, 2, 2)
  updateValueMap(valueMap, 'co2Unit', getCo2UnitLabel(t))
  updateValueMap(valueMap, 'co2PerYearWithUnitLabel', co2AmountPerYear.text)
  updateValueMap(valueMap, 'savedCo2PerYearWithUnitLabel', savedCo2AmountPerYear.text)
  updateValueMap(valueMap, 'co2EmissionsPowerGeneration', getCo2EmissionsPowerGeneration(project, t).text)

  const energyAmount = getEnergyAmount(project, t, 2, 2)
  updateValueMap(valueMap, 'energyAmount', energyAmount.text)

  updateValueMap(valueMap, 'nLeakages', `${leakages.length}`)

  const totalCost = totalCosts(leakages, project, t, 2, 2)
  const savedCost = totalCosts(resolvedLeakages, project, t, 2, 2)

  const totalSumsForLeakage = calcTotalSumsForLeakages(leakages, project, t)

  updateValueMap(valueMap, 'nCompleted', `${resolvedLeakages.length}`)
  updateValueMap(valueMap, 'savedLeakageRelative', calcRelative(resolvedLeakages.length, leakages.length, t, 2, 2).text)

  const resLeakageNm3Total = convertAmount(
    `${totalSumsForLeakage?.find((total) => total?.sumAmount?.unitAsEnum === Unit.LTR_MIN)?.sumAmount?.result || 0}`,
    Unit.LTR_MIN,
    Unit.NM3_HOUR,
    t,
    2,
    2,
  )
  updateValueMap(valueMap, 'resLeakageNm3Total', resLeakageNm3Total.text)
  updateValueMap(valueMap, 'showResLeakageNm3Total', resLeakageNm3Total.result > 0 ? 'true' : '')

  const resLeakageNm3Saved = convertAmount(
    `${
      totalSumsForLeakage?.find((total) => total?.savedAmount?.unitAsEnum === Unit.LTR_MIN)?.savedAmount?.result || 0
    }`,
    Unit.LTR_MIN,
    Unit.NM3_HOUR,
    t,
    2,
    2,
  )
  updateValueMap(valueMap, 'resLeakageNm3Saved', resLeakageNm3Saved.text)
  updateValueMap(valueMap, 'showResLeakageNm3Saved', resLeakageNm3Saved.result > 0 ? 'true' : '')

  updateValueMap(valueMap, 'energyPerYear', getTotalEnergy())
  updateValueMap(valueMap, 'savedEnergyPerYear', getSavedEnergy())
  updateValueMap(valueMap, 'volumePerYear', getTotalVolume())
  updateValueMap(valueMap, 'savedVolumePerYear', getSavedVolume())

  /* updateValueMap(
    valueMap,
    'energyPerYear',
    calcEnergyUsagePerYear(totalSumsForLeakage, project, t, 'sumAmount', 2, 2)?.text || '',
  )
  updateValueMap(
    valueMap,
    'savedEnergyPerYear',
    calcEnergyUsagePerYear(totalSumsForLeakage, project, t, 'savedAmount', 2, 2)?.text || '',
  ) */
  updateValueMap(
    valueMap,
    'co2Amount',
    convertProjectSpecificPower(project?.unitV || '', project?.unitV || '', project?.energyAmount || '', t, 2, 2).text,
  )

  // Excel
  updateValueMap(valueMap, 'sumAmount', totalSumsForLeakage.map((sums) => sums?.sumAmount?.text).join(' '))
  updateValueMap(valueMap, 'savedAmount', totalSumsForLeakage.map((sums) => sums?.savedAmount?.text).join(' '))
  updateValueMap(
    valueMap,
    'savedAmountPercent',
    totalSumsForLeakage.map((sums) => sums?.savedAmountPercent?.text).join(' '),
  )
  updateValueMap(valueMap, 'totalCost', totalCost.map((totalCost) => totalCost.text).join(' '))
  updateValueMap(valueMap, 'savedCost', savedCost.map((savedCost) => savedCost.text).join(' '))
  const translations = {
    sheet1: t('report.sheet1'),
    sheet2: t('report.sheet2'),
    sheet3: t('report.sheet3'),
    sheet4: t('report.sheet4'),
  }
  //console.log({ leakages })
  return {
    templateFile: type === 'xlsx' ? TEMPLATE_FILENAME_XLSX : TEMPLATE_FILENAME_HTML,
    valueMap,
    multiValueMap: generateMultiValueMap(leakages, buildings, company, project, t, 2, 2),
    type,
    totalCosts: totalCost,
    savedCosts: savedCost,
    totalSumsForLeakage,
    translations,
  }
}
